// semeia-disable-reexport
import { tracked } from "@glimmer/tracking";

export interface ErrorResponse {
  name: string;
  errorMessage: string;
}

export interface DmpConnectTlsiError extends DmpConnectError {
  s_apiErrorTlsiErrorSoapReason: string;
  s_apiErrorTlsiErrorDetailError: string;
}

export interface DmpConnectError extends Error {
  i_apiErrorCode: number;
  s_apiErrorDescription: string;
  s_apiErrorContext: string;
  s_status: string;
  s_apiErrorExtendedInformations: string;
}

export interface Result {
  error?: DmpConnectError;
}
// Types are adapted from http://localhost:10007/swagger-ui.html#/dmpc-controller/.

export interface MatriculeINS {
  identifiantSysteme: InsOid;
  valeur: string;
}

export interface DmpParameters {
  fonctionsGestionMineurs: boolean;
  cumulInvisiblePatientMasquePs: boolean;
  ageMajorite: number;
  hrPeriodeMaxMois: number;
  production: boolean;
}

export type QueryType =
  | "FindDocuments"
  | "FindSubmissionSetsAndGetContents"
  | "GetDocuments";

export interface DMPCContext {
  author?: DMPCPersonnelSante;
  confidentialityCode?: DMPCCode;
  modeAccess?: DMPCModeAcces;
}

type DMPCPersonnelSante = {
  structureSante?: {
    nom?: string;
    idNational?: string;
  };
  secteurActivite?: string;
  specialite?: string;
};

interface DMPCCode {
  valeur: string;
  identifiantNomenclature: string;
  libelle: string;
}

export interface INSICContext {
  context: {
    auteur: object;
    structure: {
      identifiantFacturation: string;
    };
  };
}

export type FindINSIWithoutVitale = INSICContext;

interface DMPCModeAcces {
  acces: "NORMAL" | "BRIS_DE_GLACES";
  raison: string;
}
export interface Beneficiary {
  ident: {
    nomPatronymique: string;
    nomUsuel: string;
    prenomUsuel: string;
    naissance: {
      //22101993
      date: string;
    };
    //"1931079191366 31"
    nir: string;
    rangDeNaissance: string;
  };
  amo: {
    qualBenefLibelle: "Assuré" | "Enfant";
  };
}

export enum DMPConnectSex {
  UnknownSex = 1,
  Male = 2,
  Female = 3
}

export type InsPatient = {
  birthName: string; // The last name of the patient
  givenName: string; // The birth first names of the patient separated by a space
  sex: DMPConnectSex;
  birthDate: string; // format "DDDD-MM-YY" - "2022-02-14"
  birthPlace?: string;
};
export interface PatientHash {
  firstName: string;
  lastName: string;
  birthName?: string;
  birthday?: Date;
  socialSecurityNumber: string;
  insuredSocialSecurityNumber?: string;
  birthrank?: number;
  gender?: "male" | "female";
}

interface SmartCard {
  name: string;
  /** The index of the card in the DMPConnectJS server */
  index: number;
}

export interface DetectCardResult {
  cps?: SmartCard;
  vitale?: SmartCard;
}

export type CPSCardRemovalObserver = unknown;
export type CPSCardRemovalCallback = () => void;

export interface GetPcsReaderResult {
  error: DmpConnectError;
  Readers: Array<{
    i_accessMode: number;
    i_slotType: number;
    s_accessMode: string;
    s_slotType: string;
    s_name: string;
  }>;
}

export interface ReadCpxCardResult {
  error?: DmpConnectError;
  PracticeLocations: Array<{
    s_practiceLocationActivity: string;
    s_practiceLocationHealthcareSettings: string;
    s_practiceLocationName: string;
    s_practiceLocationBillingNumber: string;
    s_practiceLocationStructureId: string;
  }>;
  i_remainingPinCodeInputs: number;
  s_cpxSerialNumber: string;
  s_cpxValidityDate: string;
  s_given: string;
  s_internalId: string;
  s_name: string;
  s_profession: string;
  s_professionOid: string;
  s_professionDescription: string;
  s_speciality: string;
  s_specialityDescription: string;
  s_status: string;
}

enum VitaleCardPatientQuality {
  Assure = 0,
  Ascendants = 1,
  Conjoint = 2,
  ConjointDivorce = 3,
  Concubin = 4,
  ConjointSepare = 5,
  Enfant = 6,
  ConjointVeuf = 8,
  Autre = 9
}

export interface VitaleCardPatient {
  i_birthRank: number;
  i_quality: VitaleCardPatientQuality;
  s_birthname: string;
  s_name: string;
  s_given: string;
  s_birthday: string;
  s_nir: string;
  s_certifiedNir: string;
  s_insC?: string;
}

export interface ReadVitaleCardResult {
  error?: DmpConnectError;
  Patients: Array<VitaleCardPatient>;
  i_cardStatus?: number; // See VitaleStatus enum.
  s_cardStatusText?: string;
  s_status?: string;
}

export interface VitalCardInformation {
  PatientData: {
    s_name: string;
    s_birthName: string;
    s_givenName: string;
    s_birthday: string; // May be 6 digits-long. May be defined in lunar calendar.
    i_rank: number;
    i_qualBenef: number;
    NirData: {
      Nir: {
        s_nir: string;
        s_key: string;
      };
      CertifiedNir: {
        s_nir: string;
        s_key: string;
      };
      s_certificationDate: string;
    };
    HealthCoverage: {
      s_regime: string; // E.g. "01"
      s_desk: string; // E.g. "349"  a.k.a "Organisme", "Caisse".
      s_center: string; // E.g. "9881" a.k.a "Entité de gestion", "Centre".
    };
  };
  VitaleData: {
    s_cardType: "R" | "T" | "D"; // 'R', 'T', 'D': Réelle, Test, Démo.
    s_serialNumber: string; // E.g. "467129252".
    s_validityEndDate: string; // E.g. "31122015". May not be defined.
  };
}

export interface InsiConnectorResult {
  error?: DmpConnectError;
  s_instanceUniqueId: string;
  i_connectionHandle: number;
  s_status: string;
}

export enum DmpConnectJSInsIdentityResultCode {
  InsIdentityResult_NoIdentityFound = 1,
  InsIdentityResult_SingleIdentityFound = 2,
  InsIdentityResult_MultipleIdentitiesFound = 3
}

export interface DmpConnectJSInsIdentityInfos {
  s_value: string;
  s_key: string;
  s_oid: string;
  s_startDate: string; // Format YYYY-MM-DD
  s_endDate: string; // Format YYYY-MM-DD
}

export interface DmpConnectJSInsIdentityResult extends Result {
  error?: DmpConnectTlsiError;
  s_status: string;
  i_insIdentityResult: DmpConnectJSInsIdentityResultCode;

  //  Present only if i_insIdentityResult is (2) InsIdentityResult_SingleIdentityFound
  Identity?: {
    Ins: DmpConnectJSInsIdentityInfos;
    s_birthName: string;
    s_given: string;
    s_birthGiven: string;
    i_sex: number;
    s_birthDate: string; // Format YYYY-MM-DD
    s_birthPlace: string; // Format COG ('Code Officiel Géographique' : https://www.insee.fr/fr/information/2560452)
    s_lastInsiCallDate: string; // Format YYYY-MM-DD
    History?: // (*) Only present if there is an history

    DmpConnectJSInsIdentityInfos[];
  };
}

export interface DmpConnectFilterCriteria {
  Categories?: Record<string, string>;
  s_creationDateBottom?: string;
  s_creationDateTop?: string;
  s_serviceStartDateTop?: string;
  s_serviceStartDateBottom?: string;
  s_submissionDateTop?: string;
  s_submissionDateBottom?: string;
  i_status?: number;
  i_visibility?: number;
}

export interface DmpConnectAuthor {
  i_handle: number;
  i_hpAuthenticationMode: number;
  s_hpGiven: string;
  s_hpInternalId: string;
  s_hpName: string;
  s_hpProfession: string;
  s_hpProfessionOid: string;
  s_hpSpeciality: string;
}

export interface DmpConnectDocument {
  Authors: Array<DmpConnectAuthor>;
  i_document_Format: number;
  i_document_Status: number;
  i_document_Visibility: number;
  i_handle: number;
  s_classCode: string;
  s_documentFormatDescription: string;
  s_creationDate: string;
  s_description: string;
  s_healthCareFacilityTypeCode: string;
  s_nextUUId: string;
  s_practiceSettingCode: string;
  s_previousUUId: string;
  s_serviceStartDate: string;
  s_serviceStopDate: string;
  s_submissionDate: string;
  s_title: string;
  s_typeCode: string;
  s_uniqueId: string;
  s_uuid: string;
}

export type status = "OK" | "ERROR" | "API_ERROR";
export interface FindDocumentsReponsePayload {
  Documents: DmpConnectDocument[];
  s_status: status;
  error?: Error;
}

export interface DirectAuthenticationDMPStatusContent extends Result {
  ExistingTestAnswer: {
    i_dmpStatus: number;
    i_userAuthorization: number;
  };
}

export interface DmpConnectJSDmpDocumentContent extends Result {
  s_fileContentInBase64: string;
  s_cdaHeadersInBase64: string;
  s_cdaContentInBase64: string;
  i_documentFormat: number;
  s_documentFormatDescription: string;
}

export interface PracticeLocation {
  activity: string;
  structureName: string;
  structureId: string;
  billingNumber: string;
}

export interface CPSInformations {
  medicLastName: string;
  medicFirstName: string;
  medicInternalId: string;
  medicProfessionCode: string;

  cpxSerialNumber: string;
  cpxValidityDate: Date;

  medicCardType?: string;

  practiceLocations: PracticeLocation[];
}

export interface PatientVitaleInformation {
  firstName: string;
  birthName: string;
  lastName: string;
  birthday: Date;
  socialSecurityNumber?: string;
  insuredSocialSecurityNumber?: string;
  birthrank?: string;
  gender?: string;
}

export enum DmpAuthorizationStatus {
  Authorized = "AUTHORIZED",
  Expired = "EXPIRED",
  NotAuthorized = "NOT_AUTHORIZED"
}

export interface DirectAuthenticationStatus {
  authorizationStatus: DmpAuthorizationStatus;
}

export enum DocumentStatus {
  APPROVED = "APPROVED",
  DEPRECATED = "DEPRECATED",
  SUBMITTED = "SUBMITTED",
  ARCHIVED = "ARCHIVED",
  DELETED = "DELETED"
}

export enum DocumentVisibility {
  NORMAL = "N",
  HIDDEN_TO_PATIENT = "HIDDEN_TO_PATIENT",
  HIDDEN_TO_HEALTHCARE_PROFESSIONAL = "HIDDEN_TO_HEALTHCARE_PROFESSIONAL",
  HIDDEN_TO_LEGAL_REPRESENTATIVES = "HIDDEN_TO_LEGAL_REPRESENTATIVES"
}

export interface FindDocumentsCriteria {
  typeCodes?: Array<string>;
  serviceStartTimeFrom?: Date;
  serviceStopTimeTo?: Date;
  submissionStartTimeFrom?: Date;
  statuses?: Array<DocumentStatus>;
  visibilities?: Array<DocumentVisibility>;
}
export interface DmpDocumentMetadata {
  classCode: string;
  confidentialities: Array<DocumentVisibility>;
  creationTime: Date;
  description?: string;
  format: string;
  formatDescription?: string;
  mimeType: string;
  status: Array<DocumentStatus>;
  title: string;
  type: string;
  uniqueId: string;
  uuid: string;
}

export interface DmpDocumentContent {
  body: string | undefined;
  bodyMimeType: string | undefined;
  cdaHeaders: string | undefined;
  cdaHeadersMimeType: string | undefined;
  title: string | undefined;
  cdaContent: string | undefined;
}

export interface GetWebPsDmpUrlsPayload extends Result {
  Urls: string[];
}

export interface WebPSURLs {
  medicDashboardURL?: string;
  patientFolderURL?: string;
  patientDMPManagementURL?: string;
  documentsURL?: string;
  carePathwayURL?: string;
  historyAccessURL?: string;
  parametersURL?: string;
  rightsAndWillsURL?: string;
}

export enum InsOid {
  NIR = "1.2.250.1.213.1.4.8",
  NIA = "1.2.250.1.213.1.4.9",
  TEST = "1.2.250.1.213.1.4.10",
  DEMO = "1.2.250.1.213.1.4.11",
  UNKNOWN = "unknown"
}

export enum InsType {
  NIA = "NIA",
  NIR = "NIR",
  UNKNOWN = "NIR ou NIA"
}

export enum InsSex {
  Unknown = 1,
  Male = 2,
  Female = 3
}

export enum InsIdentityResultCode {
  InsIdentityResult_NoIdentityFound = "01",
  InsIdentityResult_SingleIdentityFound = "00",
  InsIdentityResult_MultipleIdentitiesFound = "02"
}

export interface IdentityInfos {
  value: string;
  key: string;
  oid: string;
  startDate?: Date;
  endDate?: Date;
}

export type InsIdentityResult = {
  status: string;
  result: InsIdentityResultCode;
  identity?: IdentityInfos;
  birthName?: string;
  given?: string;
  birthGiven?: string;
  sex?: string;
  birthday?: Date;
  birthPlace?: string;
  history?: IdentityInfos[];
};

export interface INS {
  ins: {
    value: string;
    oid: InsOid;
  };

  birthGivenName: string;
  birthName: string;
  sex: InsSex;
  birthDate: string;
  birthPlaceCode: string;
}

export enum InsStatus {
  Provisional = "provisional",
  Validated = "validated",
  Recovered = "recovered", // TODO: Should be called Retreived.
  Qualified = "qualified"
}

export class DmpDocumentsSearchParams {
  @tracked submissionStartTimeFrom: Date | string | undefined;

  constructor(
    public typeCodes: Array<string>,
    public serviceStartTimeFrom: string | undefined,
    public serviceStopTimeTo: string | undefined,
    submissionStartTimeFrom: Date | string | undefined,
    public archived: boolean,
    public obsolete: boolean,
    public hiddenToPatient: boolean,
    public hiddenToHealthcareProfessional: boolean,
    public hiddenToLegalRepresentatives: boolean
  ) {
    this.submissionStartTimeFrom = submissionStartTimeFrom;
  }
}
